var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import { v4 as uuidv4 } from 'uuid';
import orchestratorApiClient from './orchestrator-api-client';
// ---------------------------------------------------------------------------
// Error → user-facing message
// Turns a failed chat request into plain-English copy the widget can render,
// so failures never silently hang. Surfaces the real reason for a 422
// validation error (e.g. message too long) instead of a generic retry prompt.
// No technical detail (status codes, stack traces) ever reaches the user.
// ---------------------------------------------------------------------------
export const GENERIC_SEND_ERROR = "Sorry — your message couldn't be sent just now. Please try again in a moment.";
// Shown when the orchestrator deduped a send and no reply ever replays for it:
// the earlier answer is the answer, so say that rather than leave the send
// unacknowledged. See useChatInput's duplicate branch.
export const DUPLICATE_SEND_MESSAGE = "That's the same question you just asked, so I haven't run it again — the earlier answer is above in this conversation. Rephrase it or start a new chat if you'd like a fresh response.";
export function describeChatSendError(err) {
    var _a, _b;
    const response = err === null || err === void 0 ? void 0 : err.response;
    if ((response === null || response === void 0 ? void 0 : response.status) === 422 && Array.isArray((_a = response.data) === null || _a === void 0 ? void 0 : _a.detail)) {
        const tooLong = response.data.detail.find((d) => (d === null || d === void 0 ? void 0 : d.type) === 'string_too_long' && Array.isArray(d.loc) && d.loc.includes('message'));
        if (tooLong) {
            const max = (_b = tooLong.ctx) === null || _b === void 0 ? void 0 : _b.max_length;
            return max
                ? `Your message is too long (the limit is ${max.toLocaleString()} characters). Please shorten it and try again.`
                : 'Your message is too long. Please shorten it and try again.';
        }
    }
    return GENERIC_SEND_ERROR;
}
// Transform camelCase v1 PageContext to snake_case API payload.
// ONLY called for v1 payloads. Exported for unit-testing.
export function toPageContextPayload(ctx) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    return {
        schema_version: 1,
        page_type: (_a = ctx.pageType) !== null && _a !== void 0 ? _a : 'unknown',
        page_path: (_b = ctx.pagePath) !== null && _b !== void 0 ? _b : null,
        building_id: (_c = ctx.buildingId) !== null && _c !== void 0 ? _c : null,
        building_name: (_d = ctx.buildingName) !== null && _d !== void 0 ? _d : null,
        territory_id: (_e = ctx.territoryId) !== null && _e !== void 0 ? _e : null,
        inspection_id: (_f = ctx.inspectionId) !== null && _f !== void 0 ? _f : null,
        inspection_label: (_g = ctx.inspectionLabel) !== null && _g !== void 0 ? _g : null,
        inspection_series_id: (_h = ctx.inspectionSeriesId) !== null && _h !== void 0 ? _h : null,
        work_order_id: (_j = ctx.workOrderId) !== null && _j !== void 0 ? _j : null,
        work_order_number: (_k = ctx.workOrderNumber) !== null && _k !== void 0 ? _k : null,
        proposal_id: (_l = ctx.proposalId) !== null && _l !== void 0 ? _l : null,
        report_id: (_m = ctx.reportId) !== null && _m !== void 0 ? _m : null,
        account_id: (_o = ctx.accountId) !== null && _o !== void 0 ? _o : null,
        // Phase B new entity fields
        deficiency_id: (_p = ctx.deficiencyId) !== null && _p !== void 0 ? _p : null,
        contact_id: (_q = ctx.contactId) !== null && _q !== void 0 ? _q : null,
        invoice_id: (_r = ctx.invoiceId) !== null && _r !== void 0 ? _r : null,
        device_id: (_s = ctx.deviceId) !== null && _s !== void 0 ? _s : null,
        device_type: (_t = ctx.deviceType) !== null && _t !== void 0 ? _t : null
    };
}
/**
 * Build the wire-ready `page_context` payload from a validated PageContext.
 *
 * v2 (schema_version === 2): pass the entire object through unmodified —
 *   the producer (iOS) owns the snake_case envelope; the widget must not
 *   re-shape it. No camelCase translation is applied.
 *
 * v1 (anything else): apply camelCase → snake_case translation via
 *   toPageContextPayload() and stamp schema_version: 1 explicitly.
 */
export function serializePageContext(ctx) {
    if (ctx.schema_version === 2) {
        return ctx;
    }
    return toPageContextPayload(ctx);
}
export function listConversations() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield orchestratorApiClient.get('/chat');
            return response.data.conversations;
        }
        catch (error) {
            console.error('[chatApi] Error listing conversations:', error);
            throw error;
        }
    });
}
// ---------------------------------------------------------------------------
// Create Chat
// ---------------------------------------------------------------------------
export function createChat(request) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const apiPayload = {
                message: request.message,
                file_ids: request.file_ids,
                page_context: request.page_context ? serializePageContext(request.page_context) : undefined,
                training_mode: request.training_mode
            };
            const response = yield orchestratorApiClient.post('/chat', apiPayload);
            return response.data;
        }
        catch (error) {
            console.error('[chatApi] Error creating chat:', error);
            throw error;
        }
    });
}
// ---------------------------------------------------------------------------
// Approval Resume — POST /api/chat/{thread_id}/resume (orchestrator, JWT auth)
// ---------------------------------------------------------------------------
export function submitApproval(conversationId, payload) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            yield orchestratorApiClient.post(`/chat/${conversationId}/resume`, payload);
        }
        catch (error) {
            console.error('[chatApi] Error submitting approval:', error);
            throw error;
        }
    });
}
// ---------------------------------------------------------------------------
// Undo — POST /api/chat/{thread_id}/undo (orchestrator, JWT auth)
// ---------------------------------------------------------------------------
export function submitUndo(request) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield orchestratorApiClient.post(`/chat/${request.conversation_id}/undo`, {
                action_id: request.action_id
            });
            return response.data;
        }
        catch (error) {
            console.error('[chatApi] Error submitting undo:', error);
            throw error;
        }
    });
}
// Poll a queued bulk undo. `undoOperationId` is the undo_bulk_operation_id from
// submitUndo — the undo runs as its own bulk operation (IP-15237 follow-up).
export function getUndoStatus(conversationId, undoOperationId) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield orchestratorApiClient.get(`/chat/${conversationId}/undo/${undoOperationId}/status`);
        return response.data;
    });
}
export const UNDO_POLL_INTERVAL_MS = 2000;
// A criteria batch can be up to 10,000 records and each revert is a per-record
// save, so the ceiling is generous. Timing out throws rather than resolving:
// resolving would show "Undone" for a run whose outcome we never observed.
export const UNDO_POLL_TIMEOUT_MS = 15 * 60000;
export class UndoPollAbortedError extends Error {
    constructor() {
        super('Bulk undo polling aborted');
        this.name = 'UndoPollAbortedError';
    }
}
/**
 * Resolves when a queued bulk undo finishes, throws if it fails or times out.
 *
 * Retrying after a failure is safe — the processor treats an already-reverted
 * action as a success — so throwing lets UndoButton reset to an actionable
 * "Undo" instead of a "Undone" the records don't back up.
 *
 * `signal` must be supplied by any caller that can be unmounted or have its
 * conversation switched underneath it: at one request every 2s for up to 15
 * minutes this is 450 requests against a conversation the user has left.
 * Aborting stops the loop and the pending sleep immediately.
 */
export function pollUndoUntilSettled(conversationId_1, undoOperationId_1) {
    return __awaiter(this, arguments, void 0, function* (conversationId, undoOperationId, { intervalMs = UNDO_POLL_INTERVAL_MS, timeoutMs = UNDO_POLL_TIMEOUT_MS, signal } = {}) {
        var _a, _b;
        const deadline = Date.now() + timeoutMs;
        for (;;) {
            if (signal === null || signal === void 0 ? void 0 : signal.aborted)
                throw new UndoPollAbortedError();
            // eslint-disable-next-line no-await-in-loop
            const status = yield getUndoStatus(conversationId, undoOperationId);
            if (status.status === 'completed')
                return status;
            if (status.status === 'failed' || status.status === 'partial_failure') {
                throw new Error(`Bulk undo ${status.status}: ${(_a = status.succeeded_items) !== null && _a !== void 0 ? _a : 0} of ${(_b = status.total_items) !== null && _b !== void 0 ? _b : 0} reverted`);
            }
            if (Date.now() >= deadline) {
                throw new Error(`Bulk undo did not finish within ${Math.round(timeoutMs / 60000)} minutes`);
            }
            // eslint-disable-next-line no-await-in-loop
            yield new Promise((resolve, reject) => {
                const timer = setTimeout(() => {
                    signal === null || signal === void 0 ? void 0 : signal.removeEventListener('abort', onAbort);
                    resolve();
                }, intervalMs);
                function onAbort() {
                    clearTimeout(timer);
                    reject(new UndoPollAbortedError());
                }
                if (signal)
                    signal.addEventListener('abort', onAbort, { once: true });
            });
        }
    });
}
// ---------------------------------------------------------------------------
// Feedback — POST /api/chat/{conversation_id}/feedback (orchestrator, JWT auth)
// ---------------------------------------------------------------------------
export function submitFeedback(conversationId, payload) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            yield orchestratorApiClient.post(`/chat/${conversationId}/feedback`, payload);
        }
        catch (error) {
            console.error('[chatApi] Error submitting feedback:', error);
            throw error;
        }
    });
}
// ---------------------------------------------------------------------------
// Form Submit — POST /api/chat/{thread_id}/form (orchestrator, JWT auth)
// ---------------------------------------------------------------------------
export function submitForm(threadId, payload) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            yield orchestratorApiClient.post(`/chat/${threadId}/form`, payload);
        }
        catch (error) {
            console.error('[chatApi] Error submitting form:', error);
            throw error;
        }
    });
}
// ---------------------------------------------------------------------------
// Cancel Response — POST /api/chat/{conversation_id}/cancel (orchestrator, JWT auth)
// ---------------------------------------------------------------------------
export function cancelResponse(conversationId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield orchestratorApiClient.post(`/chat/${conversationId}/cancel`);
            return response.data;
        }
        catch (error) {
            console.error('[chatApi] Error cancelling response:', error);
            throw error;
        }
    });
}
export function getMessages(sessionId, options) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const params = {};
            if (options === null || options === void 0 ? void 0 : options.before)
                params.before = options.before;
            if ((options === null || options === void 0 ? void 0 : options.limit) !== undefined) {
                const parsedLimit = Number(options.limit);
                if (Number.isFinite(parsedLimit)) {
                    params.limit = Math.max(1, Math.min(parsedLimit, 50));
                }
            }
            const requestConfig = Object.keys(params).length > 0 ? { params } : undefined;
            const response = yield orchestratorApiClient.get(`/chat/${sessionId}/messages`, requestConfig);
            return response.data;
        }
        catch (error) {
            console.error('[chatApi] Error fetching messages:', error);
            throw error;
        }
    });
}
// ---------------------------------------------------------------------------
// Send Message — POST /api/chat/{session_id}/messages (orchestrator, JWT auth)
// The orchestrator handles streaming; Rails owns the persistence via callbacks.
// ---------------------------------------------------------------------------
/**
 * Mint an idempotency key for ONE send attempt.
 *
 * Must be called where the user acts (useChatInput.handleSubmit) and then held
 * on the request, never regenerated inside the transport: a retry of the same
 * POST has to carry the SAME id for the orchestrator to recognise it as a
 * retry, while a deliberate re-ask mints a new one and is answered again.
 *
 * uuid v4 satisfies the server's 1–128 char [A-Za-z0-9_-] constraint.
 */
export function newClientRequestId() {
    // uuid@9 ships no bundled types here, so v4() is `any` — pin it to string
    // rather than let `any` leak into the request payload.
    return uuidv4();
}
export function sendMessage(sessionId, request, signal) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const apiPayload = {
                message: request.message,
                file_ids: request.file_ids,
                page_context: request.page_context ? serializePageContext(request.page_context) : undefined,
                training_mode: request.training_mode,
                client_request_id: request.client_request_id
            };
            const response = yield orchestratorApiClient.post(`/chat/${sessionId}/messages`, apiPayload, signal ? { signal } : undefined);
            return response.data;
        }
        catch (error) {
            console.error('[chatApi] Error sending message:', error);
            throw error;
        }
    });
}
